/**
 * Utility functions for handling cart-related errors, especially IndexedDB issues
 */

export interface CartErrorOptions {
  showReloadOption?: boolean
  autoReloadDelay?: number
  customMessage?: string
}

/**
 * Handles cart errors with special handling for IndexedDB connection issues
 */
export function handleCartError(
  error: any,
  operation: string,
  options: CartErrorOptions = {}
): string {
  const {
    showReloadOption = true,
    autoReloadDelay = 2000,
    customMessage
  } = options

  const errorMessage = error.message || error.toString()

  // Check if this is an IndexedDB-related error
  const isIndexedDBError = errorMessage.includes('IndexedDB') ||
                         errorMessage.includes('Connection to Indexed Database') ||
                         errorMessage.includes('The operation failed for an unknown transient reason')

  if (isIndexedDBError) {
    console.warn(`IndexedDB error in ${operation}:`, errorMessage)

    if (showReloadOption && autoReloadDelay > 0) {
      // Auto-reload after delay for IndexedDB errors
      setTimeout(() => {
        window.location.reload()
      }, autoReloadDelay)
    }

    return customMessage || "We're having trouble loading your cart. Please refresh the page."
  }

  // Handle other types of errors
  console.error(`Cart error in ${operation}:`, errorMessage)

  // For GraphQL errors, try to extract meaningful messages
  if (error.graphQLErrors && error.graphQLErrors.length > 0) {
    return error.graphQLErrors[0].message || "An error occurred while updating your cart."
  }

  if (error.networkError) {
    return "Network error. Please check your connection and try again."
  }

  return customMessage || "Uh oh - we ran into an error. Please refresh and try again."
}

/**
 * Enhanced error handler for cart operations that returns both error message and recovery action
 */
export function handleCartOperationError(
  error: any,
  operation: string,
  setErrorMessage: (message: string) => void,
  options: CartErrorOptions = {}
): { message: string; shouldReload: boolean } {
  const message = handleCartError(error, operation, options)

  // Determine if we should auto-reload based on error type
  const shouldReload = error.message?.includes('IndexedDB') ||
                     error.message?.includes('Connection to Indexed Database')

  setErrorMessage(message)

  return { message, shouldReload }
}

